import React from 'react';
import { sectionTitleClass } from './primitives/inputClasses';

// Visual grouping for fields. The grid is 12 columns; child <FormField>s
// declare their width via `colSpan` (defaults to 12 = full width).
// `compact` removes the section title margin — useful inside dense modals.
// `spacing` overrides the bottom margin between sections — use a smaller
// value when stacking many sections inside a constrained viewport (e.g.
// onboarding forms in a Bootstrap card) to keep the submit button visible.
//
// IMPORTANT: section titles are rendered as a <p>, not an <h*>, on purpose.
// The form library is consumed inside dialogs and pages alike, and the base
// layer restyles every <h1>–<h6> (larger font, brand color). Using a heading
// element makes the title inherit those
// styles and breaks visual consistency between forms. Section titles in v2
// are deliberately low-emphasis labels — the modal/page title carries the
// real heading weight — so a non-heading element with the canonical
// `sectionTitleClass` is the correct semantic and visual choice. Do not
// change this back to <h3> without resolving the global heading styles.

// Tailwind JIT only emits classes that appear as complete strings — keep
// an explicit map rather than templating `mb-${N}` or `gap-${N}`.
const SPACING_CLASSES = {
  0: 'mb-0',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  5: 'mb-5',
  6: 'mb-6',
  8: 'mb-8',
};

const GAP_CLASSES = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
};

export default function FormSection({
  title,
  description,
  className = '',
  compact = false,
  spacing,
  gap,
  children,
}) {
  const spacingClass =
    spacing != null ? SPACING_CLASSES[spacing] || SPACING_CLASSES[5] : compact ? 'mb-4' : 'mb-5';
  const gapClass = gap != null ? GAP_CLASSES[gap] || GAP_CLASSES[4] : 'gap-4';
  return (
    <section className={`${spacingClass} ${className}`}>
      {title ? <p className={sectionTitleClass}>{title}</p> : null}
      {description ? <p className="text-xs text-slate-500 mb-3">{description}</p> : null}
      <div className={`grid grid-cols-12 ${gapClass}`}>{children}</div>
    </section>
  );
}
